<template>
  <div v-if="groups.length > 0 || isLive" class="flex flex-col gap-1.5">
    <div v-if="isLive" class="flex items-center gap-2 px-1">
      <span class="cleo-status-orb shrink-0" />
      <span class="cleo-shimmer-text flex-1 min-w-0 truncate text-xs font-medium">{{ statusText }}</span>
      <span class="shrink-0 text-[10px] font-medium tabular-nums" style="color: #a4adba;">
        {{ doneCalls }}/{{ totalCalls }}<template v-if="elapsedLabel"> · {{ elapsedLabel }}</template>
      </span>
    </div>

    <TransitionGroup name="cleo-step" tag="div" class="flex flex-col">
      <div v-for="(g, gi) in groups" :key="g.id" class="flex gap-2">
        <div class="relative w-[10px] shrink-0 self-stretch" aria-hidden="true">
          <span v-if="gi > 0" class="cleo-rail-line" :style="`top: 0; height: ${railTop(g)}px;`" />
          <span v-if="gi < groups.length - 1" class="cleo-rail-line" :style="`top: ${railTop(g)}px; bottom: 0;`" />
          <span v-if="gi === 0" class="cleo-rail-dot" :style="`top: ${railTop(g)}px;`" :class="isLive && g.status === 'pending' ? 'cleo-rail-dot-live' : ''" />
          <span
            v-else
            class="cleo-rail-node"
            :style="`top: ${railTop(g)}px;`"
            :class="g.kind === 'thinking' ? 'cleo-rail-node-thinking' : (g.status === 'pending' && isLive ? 'cleo-rail-node-active' : (g.status === 'error' ? 'cleo-rail-node-error' : ''))"
          />
        </div>

        <p
          v-if="g.kind === 'thinking'"
          class="cleo-thinking-note flex-1 min-w-0 text-[11px] leading-relaxed"
          :class="gi < groups.length - 1 ? 'mb-2' : ''"
          style="color: #475569; white-space: pre-wrap;"
        >{{ g.result }}</p>

        <div
        v-else
        class="cleo-tool-card flex-1 min-w-0"
        :class="[
          g.status === 'pending' && isLive ? 'cleo-tool-card-live' : '',
          gi < groups.length - 1 ? 'mb-1.5' : '',
        ]"
      >
        <button
          class="w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors focus:outline-none"
          :class="hasBody(g) ? 'hover:bg-slate-50/70' : 'cursor-default'"
          @click="hasBody(g) && toggleCard(g.id)"
        >
          <span
            class="shrink-0 w-[24px] h-[24px] flex items-center justify-center rounded-md"
            :style="{ background: metaFor(g).bg, color: metaFor(g).fg }"
          >
            <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path :d="metaFor(g).icon"/>
            </svg>
          </span>

          <span class="flex-1 min-w-0">
            <span class="block truncate text-xs font-semibold leading-tight" style="color: #334155;">{{ metaFor(g).title }}</span>
            <span
              class="block truncate text-[10px] leading-snug mt-px"
              :class="g.status === 'pending' && isLive ? 'cleo-shimmer-text font-medium' : ''"
              :style="g.status === 'pending' && isLive ? '' : 'color:#94a3b8;'"
            >{{ groupSubtitle(g) }}</span>
          </span>

          <span v-if="g.status === 'done'" class="cleo-check-pop shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style="background:#e5f4ec;">
            <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#3e8e63" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
          </span>
          <span v-else-if="g.status === 'error'" class="shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style="background:#f9ecec;">
            <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#b06060" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </span>
          <svg v-else class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" style="color:#9db0c9;">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>

          <svg
            v-if="hasBody(g)"
            class="w-3 h-3 shrink-0 transition-transform duration-200"
            :class="isCardOpen(g) ? 'rotate-90' : ''"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" style="color: #c2c9d6;"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div v-if="isCardOpen(g)" style="border-top: 1px solid rgba(15,23,42,0.05);">
          <TransitionGroup v-if="g.children.length > 0" name="cleo-step" tag="div" class="flex flex-col px-2.5 py-1">
            <div v-for="(c, ci) in g.children" :key="c.id">
              <div class="flex items-center gap-2 py-[5px]" :style="ci > 0 ? 'border-top: 1px solid rgba(15,23,42,0.04);' : ''">
                <span v-if="c.status === 'done'" class="cleo-check-pop shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center" style="background:#e5f4ec;">
                  <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#3e8e63" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                </span>
                <span v-else-if="c.status === 'error'" class="shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center" style="background:#f9ecec;">
                  <svg class="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="#b06060" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </span>
                <svg v-else class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" style="color:#9db0c9;">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>

                <span
                  class="text-[11px] font-medium leading-tight shrink-0"
                  :class="c.status === 'pending' && isLive ? 'cleo-shimmer-text' : ''"
                  :style="c.status === 'pending' && isLive ? '' : (c.status === 'error' ? 'color:#b06060;' : 'color:#475569;')"
                >{{ childLabel(c.fn) }}</span>

                <span class="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
                  <span
                    v-for="(chip, chipIdx) in chipsFor(c)"
                    :key="chipIdx"
                    class="shrink-0 max-w-[130px] truncate text-[9px] font-medium px-1.5 py-px rounded-full"
                    style="background: #f4f6f9; color: #7c8798; border: 1px solid rgba(15,23,42,0.05);"
                    :title="chip"
                  >{{ chip }}</span>
                </span>

                <button
                  v-if="hasDetails(c)"
                  class="shrink-0 p-1 rounded transition-colors hover:bg-gray-100 focus:outline-none"
                  title="Details"
                  @click="toggleDetails(c.id)"
                >
                  <svg class="w-2.5 h-2.5 transition-transform duration-150" :class="expanded.has(c.id) ? 'rotate-90' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" style="color:#c2c9d6;">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div v-if="expanded.has(c.id)" class="mb-1.5">
                <TechDetails :call="c" />
              </div>
            </div>
          </TransitionGroup>

          <div v-if="hasDetails(g)" class="px-2.5 pb-2" :class="g.children.length === 0 ? 'pt-2' : ''">
            <button
              v-if="g.kind === 'agent'"
              class="flex items-center gap-1 text-[10px] font-medium py-1 focus:outline-none"
              style="color:#8b94a3;"
              @click="toggleDetails(g.id)"
            >
              <svg class="w-2.5 h-2.5 transition-transform duration-150" :class="expanded.has(g.id) ? 'rotate-90' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" style="color:#c2c9d6;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              Briefing & result
            </button>
            <TechDetails v-if="g.kind !== 'agent' || expanded.has(g.id)" :call="g" />
          </div>
        </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, defineComponent, h } from 'vue';
import type { PropType } from 'vue';

export interface ToolPanelEvent {
  event: string;
  plugin: string;
  fn: string;
  args?: Record<string, string> | null;
  result?: string | null;
  error?: string | null;
}

interface CallInfo {
  id: string;
  plugin: string;
  fn: string;
  args?: Record<string, string>;
  result?: string;
  error?: string;
  status: 'pending' | 'done' | 'error';
}

interface StepGroup extends CallInfo {
  kind: 'planning' | 'agent' | 'nav' | 'title' | 'tool' | 'thinking';
  children: CallInfo[];
}

const props = defineProps<{
  events: ToolPanelEvent[];
  isLive?: boolean;
}>();

const expanded = ref<Set<string>>(new Set());
const expandedCards = ref<Set<string>>(new Set());

const toggleDetails = (id: string): void => {
  if (expanded.value.has(id)) {
    expanded.value.delete(id);
  } else {
    expanded.value.add(id);
  }
};

const toggleCard = (id: string): void => {
  if (expandedCards.value.has(id)) {
    expandedCards.value.delete(id);
  } else {
    expandedCards.value.add(id);
  }
};

// ── Elapsed timer (only ticks for turns observed live) ──────────────────────
const startedAt = ref<number | null>(null);
const nowTick = ref(Date.now());
const frozenElapsedMs = ref<number | null>(null);
let tickTimer: ReturnType<typeof setInterval> | null = null;

watch(() => props.isLive, (live) => {
  if (live && startedAt.value === null) {
    startedAt.value = Date.now();
    tickTimer = setInterval(() => { nowTick.value = Date.now(); }, 250);
  }
  if (!live && tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
    if (startedAt.value !== null) frozenElapsedMs.value = Date.now() - startedAt.value;
  }
}, { immediate: true });

onUnmounted(() => { if (tickTimer) clearInterval(tickTimer); });

const elapsedMs = computed((): number => {
  if (startedAt.value === null) return 0;
  return (props.isLive ? nowTick.value : (startedAt.value + (frozenElapsedMs.value ?? 0))) - startedAt.value;
});

const elapsedLabel = computed((): string => {
  if (startedAt.value === null) return '';
  return `${Math.floor(elapsedMs.value / 1000)}s`;
});

// ── Icons (heroicons outline, merged path data) ─────────────────────────────
const ICONS: Record<string, string> = {
  map: 'M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z',
  building: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z',
  truck: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
  globe: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
  cube: 'm21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9',
  banknotes: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  document: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  megaphone: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  adjust: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75',
  mail: 'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
  pin: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  link: 'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
  tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z M6 6h.008v.008H6V6z',
  spark: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z',
};

// ── Domain color pairs ───────────────────────────────────────────────────────
const COLORS = {
  violet:  { bg: '#f3e8ff', fg: '#7c3aed' },
  blue:    { bg: '#e8f0fe', fg: '#004AAD' },
  orange:  { bg: '#ffedd5', fg: '#ea580c' },
  cyan:    { bg: '#cffafe', fg: '#0e7490' },
  amber:   { bg: '#fef3c7', fg: '#b45309' },
  emerald: { bg: '#d1fae5', fg: '#047857' },
  indigo:  { bg: '#e0e7ff', fg: '#4338ca' },
  pink:    { bg: '#fce7f3', fg: '#be185d' },
  slate:   { bg: '#f1f5f9', fg: '#475569' },
};

interface GroupMeta { title: string; icon: string; bg: string; fg: string; live: string }

const AGENT_META: Record<string, GroupMeta> = {
  RunCompaniesAgent:            { title: 'Companies specialist',          icon: ICONS.building,  ...COLORS.blue,    live: 'Looking up company data…' },
  RunTransportRoadAgent:        { title: 'Road transport specialist',     icon: ICONS.truck,     ...COLORS.orange,  live: 'Digging through road shipments…' },
  RunSeaAirAgent:               { title: 'Sea & Air specialist',          icon: ICONS.globe,     ...COLORS.cyan,    live: 'Scanning sea & air shipments…' },
  RunLogisticsAgent:            { title: 'Warehousing specialist',        icon: ICONS.cube,      ...COLORS.amber,   live: 'Checking warehouse records…' },
  RunFinanceAgent:              { title: 'Finance specialist',            icon: ICONS.banknotes, ...COLORS.emerald, live: 'Crunching the numbers…' },
  RunUsersAgent:                { title: 'Users & access specialist',     icon: ICONS.users,     ...COLORS.indigo,  live: 'Reviewing users and access…' },
  RunContractsAgent:            { title: 'Contracts specialist',          icon: ICONS.document,  ...COLORS.slate,   live: 'Pulling contract records…' },
  RunAccountReceivablesAgent:   { title: 'Receivables specialist',        icon: ICONS.banknotes, ...COLORS.emerald, live: 'Reviewing receivables…' },
  RunStatusesAgent:             { title: 'Status specialist',             icon: ICONS.adjust,    ...COLORS.slate,   live: 'Reading status configurations…' },
  RunDocumentationAgent:        { title: 'Platform guide specialist',     icon: ICONS.document,  ...COLORS.violet,  live: 'Looking it up in the guide…' },
  RunOptionsAgent:              { title: 'Platform options specialist',   icon: ICONS.adjust,    ...COLORS.slate,   live: 'Reading platform options…' },
  RunAnnouncementsAgent:        { title: 'Announcements specialist',      icon: ICONS.megaphone, ...COLORS.pink,    live: 'Checking announcements…' },
  RunAcceptedEmailDomainsAgent: { title: 'Email domains specialist',      icon: ICONS.mail,      ...COLORS.slate,   live: 'Checking email domains…' },
  RunActivitiesAgent:           { title: 'Activities specialist',         icon: ICONS.clock,     ...COLORS.indigo,  live: 'Reviewing activity data…' },
  RunShipmentActivitiesAgent:   { title: 'Shipment tracking specialist',  icon: ICONS.pin,       ...COLORS.orange,  live: 'Tracing shipment activity…' },
  RunCompanyProfileAgent:       { title: 'Company profile specialist',    icon: ICONS.building,  ...COLORS.blue,    live: 'Checking the company profile…' },
};

// Disambiguates child attribution when several specialists run in parallel.
const FUNCTION_AGENT: Record<string, string> = {
  GetCompanyCreditSummary: 'RunCompaniesAgent',
  GetCompanyOrdersData: 'RunCompaniesAgent',
  SearchMarketingRatios: 'RunCompaniesAgent',
  SearchCompanies: 'RunCompaniesAgent', GetCompanyWithBusinessLines: 'RunCompaniesAgent', SearchContacts: 'RunCompaniesAgent',
  SearchCountries: 'RunCompaniesAgent', GetKeyAccountManagers: 'RunCompaniesAgent', GetSalesPersons: 'RunCompaniesAgent',
  GetContractManagers: 'RunCompaniesAgent',
  GetCompanyDomesticOrderEntry: 'RunCompaniesAgent', GetCompanyInternationalOrderEntry: 'RunCompaniesAgent',
  SearchDomesticShipments: 'RunTransportRoadAgent', SearchInternationalShipments: 'RunTransportRoadAgent', SearchStrongoShipments: 'RunTransportRoadAgent',
  SearchSeaShipments: 'RunSeaAirAgent', SearchAirShipments: 'RunSeaAirAgent',
  SearchInbounds: 'RunLogisticsAgent', SearchOutbounds: 'RunLogisticsAgent', SearchVbsInbounds: 'RunLogisticsAgent', SearchVbsOutbounds: 'RunLogisticsAgent',
  SearchCustomerLedgerEntries: 'RunFinanceAgent', GetCustomerLedgerEntryWithGlEntries: 'RunFinanceAgent',
  SearchCustomerLedgerGlEntries: 'RunFinanceAgent',
  GetErpInvoiceLineNames: 'RunFinanceAgent', GetGlTransportTypes: 'RunFinanceAgent', GetExchangeRates: 'RunFinanceAgent',
  GetUnpaidInvoiceSummary: 'RunFinanceAgent', RangeQuery: 'RunFinanceAgent',
  SearchUsers: 'RunUsersAgent', SearchClientUsers: 'RunUsersAgent', SearchClientRoles: 'RunUsersAgent',
  SearchClientPermissions: 'RunUsersAgent', SearchRoles: 'RunUsersAgent', SearchIndoorSales: 'RunUsersAgent',
  GetUserBusinessLineAccess: 'RunUsersAgent', GetUserCompanyAccess: 'RunUsersAgent',
  SearchAccountReceivables: 'RunAccountReceivablesAgent',
  SearchReleases: 'RunAnnouncementsAgent', SearchMaintenances: 'RunAnnouncementsAgent', SearchSiteNotifications: 'RunAnnouncementsAgent',
  SearchApiSources: 'RunStatusesAgent', SearchApiStatuses: 'RunStatusesAgent', SearchApiSubstatuses: 'RunStatusesAgent', SearchAppStatuses: 'RunStatusesAgent',
  GetDataSyncStatus: 'RunStatusesAgent',
  SearchDocumentation: 'RunDocumentationAgent',
  SearchOptionCategories: 'RunOptionsAgent', SearchOptionValues: 'RunOptionsAgent', SearchTooltipCategories: 'RunOptionsAgent', SearchTooltipValues: 'RunOptionsAgent',
  SearchAcceptedEmailDomains: 'RunAcceptedEmailDomainsAgent', SearchKlgAllowedEmailDomains: 'RunAcceptedEmailDomainsAgent', SearchWebmailDomains: 'RunAcceptedEmailDomainsAgent',
  SearchActivityTypes: 'RunActivitiesAgent', SearchActivityStatuses: 'RunActivitiesAgent',
  SearchDomesticRoadActivities: 'RunShipmentActivitiesAgent', SearchInternationalRoadActivities: 'RunShipmentActivitiesAgent', SearchStrongoActivities: 'RunShipmentActivitiesAgent',
  GetMyBusinessLines: 'RunCompanyProfileAgent', GetMyCompanyProfile: 'RunCompanyProfileAgent',
  GetDomesticOrderEntrySettings: 'RunCompanyProfileAgent', GetInternationalOrderEntrySettings: 'RunCompanyProfileAgent',
  GetMyOrderEntryAddresses: 'RunCompanyProfileAgent', GetMyLeadtimes: 'RunCompanyProfileAgent',
  GetCreditAndUnpaidSummary: 'RunFinanceAgent', SearchInvoices: 'RunFinanceAgent',
  GetUser: 'RunUsersAgent',
  GetCompanyContracts: 'RunContractsAgent',
  SearchCurrencies: 'RunCurrenciesAgent', SearchDepartments: 'RunDepartmentsAgent',
  SearchLegalHolidays: 'RunLegalHolidaysAgent', SearchShippers: 'RunShippersAgent',
  SearchIncoterms: 'RunOrderEntryAgent', SearchPackages: 'RunOrderEntryAgent', SearchOrderEntryFeatures: 'RunOrderEntryAgent',
  GetDomesticOrderEntryGeneralSettings: 'RunOrderEntryAgent', GetInternationalOrderEntryGeneralSettings: 'RunOrderEntryAgent',
  GetStrongoOrderEntryGeneralSettings: 'RunOrderEntryAgent',
};

const CHILD_LABEL: Record<string, string> = {
  GetCompanyCreditSummary: 'Credit & balance',
  GetCompanyOrdersData: 'Orders data',
  SearchMarketingRatios: 'Marketing ratios',
  SearchCompanies: 'Companies', GetCompanyWithBusinessLines: 'Company services', SearchContacts: 'Contacts', SearchCountries: 'Countries',
  GetKeyAccountManagers: 'Key account managers', GetSalesPersons: 'Sales persons', SearchIndoorSales: 'Indoor sales',
  GetContractManagers: 'Contract managers', GetCompanyDomesticOrderEntry: 'Domestic order entry', GetCompanyInternationalOrderEntry: 'International order entry',
  SearchDomesticShipments: 'Domestic shipments', SearchInternationalShipments: 'International shipments', SearchStrongoShipments: 'Strongo shipments',
  SearchSeaShipments: 'Sea shipments', SearchAirShipments: 'Air shipments',
  SearchInbounds: 'Inbound orders', SearchOutbounds: 'Outbound orders', SearchVbsInbounds: 'VBS inbounds', SearchVbsOutbounds: 'VBS outbounds',
  SearchCustomerLedgerEntries: 'Invoices', GetCustomerLedgerEntryWithGlEntries: 'Invoice breakdown', GetErpInvoiceLineNames: 'Invoice line names',
  SearchCustomerLedgerGlEntries: 'Cost per service',
  GetGlTransportTypes: 'Transport types', GetExchangeRates: 'Exchange rates', GetUnpaidInvoiceSummary: 'Unpaid summary', RangeQuery: 'Range query',
  SearchUsers: 'KLG users', SearchClientUsers: 'Client users', SearchClientRoles: 'Client roles', SearchClientPermissions: 'Client permissions',
  SearchRoles: 'Roles', GetUser: 'User details', GetUserBusinessLineAccess: 'Business line access', GetUserCompanyAccess: 'Company access',
  SearchAccountReceivables: 'Account receivables',
  SearchReleases: 'Releases', SearchMaintenances: 'Maintenance windows', SearchSiteNotifications: 'Site notifications',
  SearchActivityTypes: 'Activity types', SearchActivityStatuses: 'Activity statuses',
  SearchDomesticRoadActivities: 'Domestic route activity', SearchInternationalRoadActivities: 'International route activity', SearchStrongoActivities: 'Strongo tracking',
  SearchCurrencies: 'Currencies', SearchDepartments: 'Departments', SearchLegalHolidays: 'Legal holidays', SearchShippers: 'Shippers',
  GetDomesticOrderEntryGeneralSettings: 'Domestic order entry settings', GetInternationalOrderEntryGeneralSettings: 'International order entry settings', GetStrongoOrderEntryGeneralSettings: 'Strongo order entry settings',
  SearchIncoterms: 'Incoterms', SearchPackages: 'Packages', SearchOrderEntryFeatures: 'Order entry features',
  SearchApiSources: 'API sources', SearchApiStatuses: 'API statuses', SearchApiSubstatuses: 'API sub-statuses', SearchAppStatuses: 'App statuses',
  GetDataSyncStatus: 'Data sync status',
  SearchDocumentation: 'Platform guide',
  SearchOptionCategories: 'Option categories', SearchOptionValues: 'Option values', SearchTooltipCategories: 'Tooltip categories', SearchTooltipValues: 'Tooltip values',
  SearchAcceptedEmailDomains: 'Accepted email domains', SearchKlgAllowedEmailDomains: 'KLG email domains', SearchWebmailDomains: 'Webmail domains',
  GetCurrentDateTime: 'Current date & time', GetCompanyContracts: 'Contracts',
  GetMyBusinessLines: 'Active services', GetMyCompanyProfile: 'Company profile',
  GetDomesticOrderEntrySettings: 'Domestic order settings', GetInternationalOrderEntrySettings: 'International order settings',
  GetMyOrderEntryAddresses: 'Order entry addresses', GetMyLeadtimes: 'Lead times',
  GetCreditAndUnpaidSummary: 'Credit & unpaid summary', SearchInvoices: 'Invoices',
};

const ACTION_TEXT: Record<string, string> = {
  ConsultAllPlanners:                  'Mapping out the best route to your answer…',
  RunCompaniesAgent:                   'Looking up company data…',
  RunTransportRoadAgent:               'Digging through road shipments…',
  RunSeaAirAgent:                      'Scanning sea & air shipments…',
  RunLogisticsAgent:                   'Checking warehouse records…',
  RunFinanceAgent:                     'Crunching the numbers…',
  RunUsersAgent:                       'Reviewing users and access…',
  RunContractsAgent:                   'Pulling contract records…',
  RunAccountReceivablesAgent:          'Reviewing receivables…',
  RunStatusesAgent:                    'Reading status configurations…',
  RunDocumentationAgent:               'Looking it up in the guide…',
  RunOptionsAgent:                     'Reading platform options…',
  RunAnnouncementsAgent:               'Checking announcements…',
  RunAcceptedEmailDomainsAgent:        'Checking email domains…',
  RunActivitiesAgent:                  'Reviewing activity data…',
  RunShipmentActivitiesAgent:          'Tracing shipment activity…',
  OfferDestinations:                   'Preparing quick links…',
  SetTitle:                            'Naming this conversation…',
  SearchCompanies:                     'Searching companies…',
  GetCompanyWithBusinessLines:         'Checking active services…',
  SearchContacts:                      'Searching contacts…',
  SearchCountries:                     'Resolving countries…',
  SearchUsers:                         'Searching users…',
  SearchClientUsers:                   'Searching client users…',
  GetKeyAccountManagers:               'Looking up key account managers…',
  GetSalesPersons:                     'Looking up sales persons…',
  SearchIndoorSales:                   'Looking up indoor sales…',
  SearchRoles:                         'Fetching roles…',
  SearchClientRoles:                   'Checking client roles…',
  SearchClientPermissions:             'Checking permissions…',
  SearchDomesticShipments:             'Searching domestic shipments…',
  SearchInternationalShipments:        'Searching international shipments…',
  SearchStrongoShipments:              'Searching Strongo shipments…',
  SearchSeaShipments:                  'Searching sea shipments…',
  SearchAirShipments:                  'Searching air shipments…',
  SearchInbounds:                      'Searching inbound orders…',
  SearchOutbounds:                     'Searching outbound orders…',
  SearchVbsInbounds:                   'Searching VBS inbounds…',
  SearchVbsOutbounds:                  'Searching VBS outbounds…',
  SearchCustomerLedgerEntries:         'Retrieving invoices…',
  GetCustomerLedgerEntryWithGlEntries: 'Loading invoice details…',
  SearchCustomerLedgerGlEntries: 'Breaking down costs per service…',
  GetErpInvoiceLineNames:              'Decoding invoice lines…',
  GetGlTransportTypes:                 'Mapping transport types…',
  GetExchangeRates:                    'Fetching exchange rates…',
  GetUnpaidInvoiceSummary:             'Summarising unpaid invoices…',
  RangeQuery:                          'Running a range query…',
  SearchAccountReceivables:            'Checking account receivables…',
  SearchReleases:                      'Checking platform releases…',
  SearchMaintenances:                  'Checking maintenance windows…',
  SearchSiteNotifications:             'Checking site notifications…',
  GetCurrentDateTime:                  'Checking the calendar…',
  SearchApiSources:                    'Fetching API sources…',
  SearchApiStatuses:                   'Fetching API statuses…',
  SearchApiSubstatuses:                'Fetching API sub-statuses…',
  SearchAppStatuses:                   'Fetching app statuses…',
  GetDataSyncStatus:                   'Checking when the data was last synced…',
  SearchDocumentation:                 'Searching the platform guide…',
  SearchOptionCategories:              'Fetching option categories…',
  SearchOptionValues:                  'Fetching option values…',
  SearchAcceptedEmailDomains:          'Checking accepted email domains…',
  SearchKlgAllowedEmailDomains:        'Checking KLG email domains…',
  SearchWebmailDomains:                'Checking webmail domains…',
  SearchActivityTypes:                 'Fetching activity types…',
  SearchActivityStatuses:              'Fetching activity statuses…',
  RunCompanyProfileAgent:              'Checking the company profile…',
  GetMyBusinessLines:                  'Checking active services…',
  GetMyCompanyProfile:                 'Loading the company profile…',
  GetDomesticOrderEntrySettings:       'Loading domestic order settings…',
  GetInternationalOrderEntrySettings:  'Loading international order settings…',
  GetMyOrderEntryAddresses:            'Loading saved addresses…',
  GetMyLeadtimes:                      'Loading lead times…',
  GetCreditAndUnpaidSummary:           'Summarising credit & unpaid amounts…',
  SearchInvoices:                      'Searching invoices…',
};

const formatFnName = (fn: string): string => fn.replace(/([A-Z])/g, ' $1').trim();

// ── Grouping: orchestrator steps become the cards, data calls nest under their specialist ──
const groups = computed<StepGroup[]>(() => {
  const gs: StepGroup[] = [];
  let seq = 0;

  const findPendingAgent = (fn: string): StepGroup | undefined => {
    const pendingAgents = gs.filter(g => g.kind === 'agent' && g.status === 'pending');
    if (pendingAgents.length === 0) return undefined;
    if (pendingAgents.length === 1) return pendingAgents[0];
    const owner = FUNCTION_AGENT[fn];
    return pendingAgents.find(g => g.fn === owner) ?? pendingAgents[pendingAgents.length - 1];
  };

  for (const ev of props.events) {
    if (ev.event === 'thinking') {
      const text = (ev.result ?? '').trim();
      if (text.length > 0) {
        gs.push({
          id: `s${seq++}`,
          plugin: ev.plugin,
          fn: ev.fn,
          result: text,
          status: 'done',
          kind: 'thinking',
          children: [],
        });
      }
    } else if (ev.event === 'tool_call') {
      const base: CallInfo = {
        id: `s${seq++}`,
        plugin: ev.plugin,
        fn: ev.fn,
        args: ev.args ?? undefined,
        status: 'pending',
      };
      if (ev.plugin === 'Execution') {
        gs.push({ ...base, kind: 'agent', children: [] });
      } else if (ev.plugin === 'Planners') {
        gs.push({ ...base, kind: 'planning', children: [] });
      } else if (ev.plugin === 'Navigation') {
        gs.push({ ...base, kind: 'nav', children: [] });
      } else if (ev.plugin === 'Title') {
        gs.push({ ...base, kind: 'title', children: [] });
      } else {
        const parent = findPendingAgent(ev.fn);
        if (parent) {
          parent.children.push(base);
        } else {
          gs.push({ ...base, kind: 'tool', children: [] });
        }
      }
    } else if (ev.event === 'tool_result' || ev.event === 'tool_error') {
      const all: CallInfo[] = [];
      for (const g of gs) {
        all.push(...g.children);
        all.push(g);
      }
      const match = [...all].reverse().find(c => c.plugin === ev.plugin && c.fn === ev.fn && c.status === 'pending');
      if (match) {
        if (ev.event === 'tool_result') {
          match.result = ev.result ?? undefined;
          match.status = 'done';
        } else {
          match.error = ev.error ?? undefined;
          match.status = 'error';
        }
      }
    }
  }
  return gs;
});

const allCalls = computed<CallInfo[]>(() => {
  const out: CallInfo[] = [];
  for (const g of groups.value) {
    if (g.kind === 'thinking') continue;
    out.push(g);
    out.push(...g.children);
  }
  return out;
});

const totalCalls = computed(() => allCalls.value.length);
const doneCalls = computed(() => allCalls.value.filter(c => c.status !== 'pending').length);

// ── Thinking (orchestrator narration) — inline notes at their chronological spot ─
const railTop = (g: StepGroup): number => (g.kind === 'thinking' ? 10 : 21);

// ── Header status line ───────────────────────────────────────────────────────
const GENERIC_PHRASES = [
  'Reading your message…',
  'Thinking it through…',
  'Rounding up the right specialists…',
  'Checking live OneClick data…',
];

const statusText = computed((): string => {
  if (props.events.length === 0) {
    const idx = Math.floor(elapsedMs.value / 2400) % GENERIC_PHRASES.length;
    return GENERIC_PHRASES[idx];
  }
  for (const g of [...groups.value].reverse()) {
    if (g.status !== 'pending') continue;
    const pendingChild = [...g.children].reverse().find(c => c.status === 'pending');
    if (pendingChild) {
      return ACTION_TEXT[pendingChild.fn] ?? `Checking ${childLabel(pendingChild.fn).toLowerCase()}…`;
    }
    return ACTION_TEXT[g.fn] ?? 'Working on it…';
  }
  return 'Putting it all together…';
});

// ── Row display helpers ──────────────────────────────────────────────────────
const metaFor = (g: StepGroup): GroupMeta => {
  if (g.kind === 'planning') return { title: 'Planning the approach', icon: ICONS.map, ...COLORS.violet, live: ACTION_TEXT.ConsultAllPlanners };
  if (g.kind === 'nav')      return { title: 'Quick links',           icon: ICONS.link, ...COLORS.blue,  live: ACTION_TEXT.OfferDestinations };
  if (g.kind === 'title')    return { title: 'Conversation title',    icon: ICONS.tag,  ...COLORS.amber, live: ACTION_TEXT.SetTitle };
  if (g.kind === 'agent') {
    return AGENT_META[g.fn] ?? {
      title: `${formatFnName(g.fn).replace(/^Run /, '').replace(/ Agent$/, '')} specialist`,
      icon: ICONS.spark, ...COLORS.blue,
      live: ACTION_TEXT[g.fn] ?? 'Working on it…',
    };
  }
  return { title: childLabel(g.fn), icon: ICONS.spark, ...COLORS.slate, live: ACTION_TEXT[g.fn] ?? 'Working on it…' };
};

const groupSubtitle = (g: StepGroup): string => {
  if (g.status === 'pending') return metaFor(g).live;
  if (g.status === 'error') return 'Something went wrong here';
  if (g.kind === 'planning') return 'Route planned';
  if (g.kind === 'nav') return 'Quick links ready';
  if (g.kind === 'title') return 'Conversation named';
  if (g.kind === 'agent') {
    const n = g.children.length;
    if (n === 0) return 'Completed';
    return `${n} data check${n === 1 ? '' : 's'} completed`;
  }
  return 'Completed';
};

const childLabel = (fn: string): string => CHILD_LABEL[fn] ?? formatFnName(fn);

const hasDetails = (c: CallInfo): boolean =>
  !!(c.error || c.result || (c.args && Object.keys(c.args).length > 0));

const hasBody = (g: StepGroup): boolean =>
  g.kind === 'agent' ? (g.children.length > 0 || hasDetails(g)) : hasDetails(g);

const isCardOpen = (g: StepGroup): boolean =>
  expandedCards.value.has(g.id) || (!!props.isLive && g.kind === 'agent' && g.status === 'pending' && g.children.length > 0);

// ── Friendly filter chips from call args ─────────────────────────────────────
const CHIP_SKIP = new Set(['page_number', 'page_size', 'fields', 'sort', 'include']);

const chipsFor = (c: CallInfo): string[] => {
  if (!c.args) return [];
  const entries: Array<[string, string]> = [];
  for (const [key, raw] of Object.entries(c.args)) {
    if (key === 'query' && typeof raw === 'string' && raw.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        for (const [k, v] of Object.entries(parsed)) {
          if (v === null || v === undefined || v === '') continue;
          entries.push([k, String(v)]);
        }
        continue;
      } catch {  }
    }
    if (raw === null || raw === undefined || raw === '') continue;
    entries.push([key, String(raw)]);
  }
  const chips = entries
    .filter(([k]) => !CHIP_SKIP.has(k))
    .map(([k, v]) => {
      const key = k.replace(/_/g, ' ');
      const val = v.length > 22 ? v.slice(0, 22) + '…' : v;
      return `${key}: ${val}`;
    });
  if (chips.length > 3) {
    return [...chips.slice(0, 3), `+${chips.length - 3}`];
  }
  return chips;
};

// ── Technical details (power-user view) ──────────────────────────────────────
const FUNCTION_ENDPOINTS: Record<string, string> = {
  GetCompanyCreditSummary: '/api/v1/companies/{id}/-actions/getCompanyInvoiceUsedCreditLimit',
  GetCompanyOrdersData: '/api/v1/companies/{id}/-actions/getOrdersData',
  SearchMarketingRatios: '/api/v1/marketing-ratios',
  SearchCompanies:              '/api/v1/companies',
  GetCompanyWithBusinessLines:  '/api/v1/companies/:companyId',
  SearchContacts:               '/api/v1/contacts',
  SearchUsers:                  '/api/v1/users',
  GetKeyAccountManagers:        '/api/v1/key-account-managers',
  GetSalesPersons:              '/api/v1/sales-persons-purchasers',
  SearchDomesticShipments:      '/api/v1/domestic-shipments',
  SearchInternationalShipments: '/api/v1/international-shipments',
  SearchStrongoShipments:       '/api/v1/strongo-shipments',
  SearchSeaShipments:           '/api/v1/tms-sea-shipments',
  SearchAirShipments:           '/api/v1/tms-air-shipments',
  SearchInbounds:               '/api/v1/inbounds',
  SearchOutbounds:              '/api/v1/outbounds',
  SearchVbsInbounds:            '/api/v1/vbs-report-inbounds',
  SearchVbsOutbounds:           '/api/v1/vbs-report-outbounds',
  SearchCustomerLedgerEntries:  '/api/v1/customer-ledger-entries',
  SearchCustomerLedgerGlEntries: '/api/v1/customer-ledger-gl-entries',
  SearchClientUsers:            '/api/v1/client-users',
  SearchClientRoles:            '/api/v1/client-roles',
  SearchClientPermissions:      '/api/v1/client-permissions',
  SearchAppStatuses:            '/api/v1/app-statuses',
  SearchOptionCategories:       '/api/v1/option-categories',
  SearchOptionValues:           '/api/v1/option-values',
  GetDataSyncStatus:            '/api/v1/api-import-results',
  SearchDocumentation:          '/api/v1/admin-documentation-texts/-actions/searchDocumentation',
};

const TOP_LEVEL_PARAMS = new Set(['sort', 'include']);

const reconstructUrl = (fn: string, args: Record<string, string> | undefined): string | null => {
  const template = FUNCTION_ENDPOINTS[fn];
  if (!template) return null;

  if (fn === 'GetCompanyWithBusinessLines' && args?.companyId) {
    return template.replace(':companyId', encodeURIComponent(args.companyId)) + '?include=companyBusinessLines';
  }

  const queryStr = args?.query;
  if (!queryStr) return template;

  let params: Record<string, string>;
  try {
    params = JSON.parse(queryStr);
  } catch {
    return template;
  }

  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    const v = encodeURIComponent(String(value));
    if (key === 'page_number')    parts.push(`page[number]=${v}`);
    else if (key === 'page_size') parts.push(`page[size]=${v}`);
    else if (TOP_LEVEL_PARAMS.has(key)) parts.push(`${key}=${v}`);
    else                          parts.push(`filter[${key}]=${v}`);
  }

  return parts.length > 0 ? `${template}?${parts.join('&')}` : template;
};

const formatJsonLoose = (src: string): string => {
  let out = '';
  let indent = 0;
  let inStr = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inStr) {
      out += ch;
      if (ch === '"' && src[i - 1] !== '\\') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; out += ch; continue; }
    if (ch === '{' || ch === '[') { indent++; out += ch + '\n' + '  '.repeat(indent); continue; }
    if (ch === '}' || ch === ']') { indent = Math.max(0, indent - 1); out += '\n' + '  '.repeat(indent) + ch; continue; }
    if (ch === ',') { out += ',\n' + '  '.repeat(indent); continue; }
    if (ch === ':') { out += ': '; continue; }
    out += ch;
  }
  return out;
};

const formatArgValue = (val: string | undefined): string => {
  if (!val) return '';
  const trimmed = val.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try { return JSON.stringify(JSON.parse(trimmed), null, 2); } catch { return formatJsonLoose(trimmed); }
  }
  return trimmed;
};

const splitTruncation = (raw: string): { body: string; note: string | null } => {
  const m = raw.match(/^([\s\S]*?)…?\s*\[(\d+) more chars\]\s*$/);
  if (m) return { body: m[1], note: `${m[2]} more characters truncated` };
  return { body: raw, note: null };
};

const truncateOutput = (val: string, limit = 1500): string => {
  const fmt = formatArgValue(val);
  return fmt.length > limit ? fmt.slice(0, limit) + '\n…' : fmt;
};

const resultBadge = (result: string): string | null => {
  const m = result.match(/"total"\s*:\s*(\d+)/);
  if (m) return `${m[1]} result${m[1] === '1' ? '' : 's'}`;
  return null;
};

const detailParams = (c: CallInfo): Array<[string, string]> => {
  if (!c.args) return [];
  const rows: Array<[string, string]> = [];
  for (const [key, raw] of Object.entries(c.args)) {
    if (key === 'query' && typeof raw === 'string' && raw.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        for (const [k, v] of Object.entries(parsed)) {
          if (v === null || v === undefined || v === '') continue;
          rows.push([k, typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)]);
        }
        continue;
      } catch {  }
    }
    rows.push([key, formatArgValue(String(raw))]);
  }
  return rows;
};

const sectionTitle = (text: string, muted = true) =>
  h('p', {
    class: 'text-[8.5px] font-semibold uppercase mb-1',
    style: `letter-spacing:0.08em; color:${muted ? '#a4adba' : '#c98a8a'};`,
  }, text);

const CLIPBOARD_PATH = 'M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184';

const TechDetails = defineComponent({
  props: {
    call: { type: Object as PropType<CallInfo>, required: true },
  },
  setup(detailProps) {
    const copied = ref<string | null>(null);
    const copyText = (key: string, text: string): void => {
      navigator.clipboard?.writeText(text);
      copied.value = key;
      setTimeout(() => { if (copied.value === key) copied.value = null; }, 1500);
    };
    const copyBtn = (key: string, text: string) => h('button', {
      class: 'shrink-0 ml-auto p-1 rounded transition-colors hover:bg-slate-200/50 focus:outline-none',
      title: 'Copy',
      onClick: () => copyText(key, text),
    }, [
      h('svg', {
        class: 'w-3 h-3',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: copied.value === key ? '#3e8e63' : '#a4adba',
        'stroke-width': copied.value === key ? '2.5' : '1.8',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      }, [h('path', { d: copied.value === key ? 'M5 13l4 4L19 7' : CLIPBOARD_PATH })]),
    ]);

    return () => {
      const c = detailProps.call;
      const sections = [];

      const url = reconstructUrl(c.fn, c.args);
      if (url) {
        sections.push(h('div', {
          class: 'flex items-center gap-1.5 min-w-0 pl-3 pr-1.5 py-1.5',
          style: 'background:#f3f6fa;',
        }, [
          h('span', {
            class: 'shrink-0 px-1.5 py-px rounded text-[8.5px] font-bold',
            style: 'background:#dbe7f5; color:#3b5f8f; letter-spacing:0.04em;',
          }, 'GET'),
          h('span', {
            class: 'truncate font-mono text-[10px]',
            style: 'color:#64748b;',
            title: url,
          }, url),
          copyBtn('url', url),
        ]));
      }

      const params = detailParams(c);
      if (params.length > 0) {
        sections.push(h('div', {
          class: 'px-3 pt-2 pb-2.5',
          style: sections.length > 0 ? 'border-top:1px solid rgba(15,23,42,0.05);' : '',
        }, [
          sectionTitle('Parameters'),
          h('div', { class: 'flex flex-col gap-1' }, params.map(([key, val], i) =>
            h('div', { key: `${key}-${i}`, class: 'flex items-start gap-2' }, [
              h('span', {
                class: 'shrink-0 w-[120px] text-[10px] leading-5',
                style: 'color:#94a3b8;',
              }, key.replace(/_/g, ' ')),
              val.includes('\n')
                ? h('pre', {
                    class: 'flex-1 min-w-0 font-mono text-[10px] rounded-md px-2 py-1.5 overflow-x-auto',
                    style: 'color:#3f4c63; background:#ffffff; border:1px solid rgba(15,23,42,0.06); white-space:pre-wrap; word-break:break-all; max-height:150px; overflow-y:auto;',
                  }, val)
                : h('span', {
                    class: 'min-w-0 font-mono text-[10.5px] rounded-md px-1.5 py-0.5',
                    style: 'color:#3f4c63; background:#ffffff; border:1px solid rgba(15,23,42,0.06); word-break:break-all;',
                  }, val),
            ]),
          )),
        ]));
      }

      if (c.result) {
        const { body, note } = splitTruncation(c.result);
        const badge = resultBadge(c.result);
        sections.push(h('div', {
          class: 'px-3 pt-2 pb-2.5',
          style: sections.length > 0 ? 'border-top:1px solid rgba(15,23,42,0.05);' : '',
        }, [
          h('div', { class: 'flex items-center gap-1.5 mb-1' }, [
            h('p', {
              class: 'text-[8.5px] font-semibold uppercase',
              style: 'letter-spacing:0.08em; color:#a4adba;',
            }, 'Response'),
            badge
              ? h('span', {
                  class: 'text-[9px] font-semibold px-1.5 py-px rounded-full',
                  style: 'background:#eef3f9; color:#5a769e;',
                }, badge)
              : null,
            copyBtn('result', c.result),
          ]),
          h('pre', {
            class: 'font-mono text-[10px] leading-relaxed rounded-md px-2 py-1.5 overflow-x-auto',
            style: 'color:#46546b; background:#ffffff; border:1px solid rgba(15,23,42,0.06); white-space:pre-wrap; word-break:break-all; max-height:240px; overflow-y:auto;',
          }, truncateOutput(body)),
          note
            ? h('p', { class: 'mt-1 text-[9px] italic', style: 'color:#a4adba;' }, `Preview only — ${note}.`)
            : null,
        ]));
      }

      if (c.error) {
        sections.push(h('div', {
          class: 'px-3 pt-2 pb-2.5',
          style: sections.length > 0 ? 'border-top:1px solid rgba(15,23,42,0.05);' : '',
        }, [
          sectionTitle('Error', false),
          h('pre', {
            class: 'font-mono text-[10px] rounded-md px-2 py-1.5',
            style: 'color:#a54848; background:#fdf9f9; border:1px solid rgba(180,80,80,0.12); white-space:pre-wrap; word-break:break-all; max-height:180px; overflow-y:auto;',
          }, c.error),
        ]));
      }

      return h('div', {
        class: 'rounded-lg overflow-hidden',
        style: 'background:#fbfcfe; border:1px solid rgba(15,23,42,0.07);',
      }, sections);
    };
  },
});
</script>

<style scoped>
/* ── Tool card (Claude-Desktop-style) ── */
.cleo-tool-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  overflow: hidden;
}
.cleo-tool-card-live {
  animation: cleo-card-live 2.4s ease-in-out infinite;
}
@keyframes cleo-card-live {
  0%, 100% { border-color: rgba(0, 74, 173, 0.1); }
  50%      { border-color: rgba(0, 74, 173, 0.28); }
}

/* ── Thinking notes (orchestrator narration, inline in the timeline) ── */
.cleo-thinking-note {
  border-left: 2px solid rgba(124, 58, 237, 0.28);
  padding-left: 8px;
  padding-top: 1px;
  padding-bottom: 1px;
}
.cleo-rail-node-thinking {
  background: #c4b5fd;
}

/* ── Timeline rail (connects the cards) ── */
.cleo-rail-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 1.5px;
  background: rgba(0, 74, 173, 0.14);
  border-radius: 1px;
}
.cleo-rail-dot {
  position: absolute;
  left: 50%;
  top: 21px;
  transform: translate(-50%, -50%);
  width: 9px;
  height: 9px;
  border-radius: 9999px;
  background: #ffffff;
  border: 2px solid #7f9cc7;
}
.cleo-rail-node {
  position: absolute;
  left: 50%;
  top: 21px;
  transform: translate(-50%, -50%);
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background: #a9bdd9;
  box-shadow: 0 0 0 1.5px #ffffff;
}
.cleo-rail-node-active {
  animation: cleo-rail-node-fill 1.6s ease-in-out infinite;
}
@keyframes cleo-rail-node-fill {
  0%, 100% { background: #dde7f3; transform: translate(-50%, -50%) scale(0.8); }
  50%      { background: #7f9cc7; transform: translate(-50%, -50%) scale(1.3); }
}
.cleo-rail-node-error {
  background: #cf9a9a;
}
.cleo-rail-dot-live {
  animation: cleo-rail-dot-pulse 1.9s ease-in-out infinite;
}
@keyframes cleo-rail-dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 74, 173, 0.2); }
  50%      { box-shadow: 0 0 0 4px rgba(0, 74, 173, 0); }
}

/* ── Status orb (live line) ── */
.cleo-status-orb {
  width: 9px;
  height: 9px;
  border-radius: 9999px;
  background: radial-gradient(circle at 32% 28%, #c9d8ee, #7f9cc7 55%, #004aad 160%);
  animation: cleo-orb-pulse 1.9s ease-in-out infinite;
}
@keyframes cleo-orb-pulse {
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(0, 74, 173, 0.2); }
  50%      { transform: scale(1.12); box-shadow: 0 0 0 5px rgba(0, 74, 173, 0); }
}

/* ── Shimmer text (thinking shimmer) ── */
.cleo-shimmer-text {
  background: linear-gradient(90deg, #9aa5b6 0%, #5a769e 42%, #46648f 50%, #5a769e 58%, #9aa5b6 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: cleo-shimmer 2.6s linear infinite;
}
@keyframes cleo-shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* ── Done check pop-in ── */
.cleo-check-pop {
  animation: cleo-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes cleo-pop {
  0%   { transform: scale(0); }
  70%  { transform: scale(1.25); }
  100% { transform: scale(1); }
}

/* ── Cards & rows slide in ── */
.cleo-step-enter-active {
  transition: opacity 0.32s cubic-bezier(0.22, 1, 0.36, 1), transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}
.cleo-step-enter-from {
  opacity: 0;
  transform: translateY(7px);
}
</style>
